<template>
  <modal-wrap
    :id="id"
    :name="id"
    :hide-footer="false"
    :hide-header-close="true"
    size="sm"
  >
    <template #modal-title>Distribution channels</template>
    <template #default>
      <ul class="list-unstyled mb-0">
        <li v-for="channel in channels" :key="channel.value" class="channel-item">
          <div class="form-check mb-2">
            <input
              :id="'channel-' + channel.value"
              v-model="channel.enabled"
              class="form-check-input"
              type="checkbox"
            />
            <label :for="'channel-' + channel.value" class="form-check-label">
              {{ channel.label }}
            </label>
          </div>
        </li>
      </ul>
    </template>
    <template #modal-footer>
      <b-button size="md" class="btn-grey mr-3" @click="closeModal"> Cancel </b-button>
      <b-button variant="success" size="md" class="btn-green" :disabled="saving" @click="save">
        Save
      </b-button>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { Company, DistributionChannelOption } from '@/types/Models/Company'
import { notify } from '@kyvg/vue3-notification'
import { BButton } from 'bootstrap-vue-next'
import { defineComponent, ref, watch, type Ref } from 'vue'
import { useCustomersStore } from '@/store/customers'
import { useVueNextModalStore } from '@/store/vueNextModal'
import ModalWrap from '@/views/Modals/ModalWrap.vue'

const ALL_CHANNELS: { value: string; label: string }[] = [
  { value: 'package', label: 'Package' },
  { value: 'parcel', label: 'Parcel' },
  { value: 'letter', label: 'Letter' },
  { value: 'box', label: 'Box' },
]

export default defineComponent({
  name: 'DistributionChannelsModal',
  components: { BButton, ModalWrap },
  props: {
    id: {
      type: String,
      required: true,
    },
    customerId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const customersStore = useCustomersStore()
    const modalStore = useVueNextModalStore()
    const saving: Ref<boolean> = ref(false)

    const channels: Ref<DistributionChannelOption[]> = ref(
      ALL_CHANNELS.map((ch) => ({ ...ch, enabled: true })),
    )

    const loadFromCustomer = (customer: Company | null) => {
      const stored = customer?.allowedDistributionChannels ?? null
      channels.value = ALL_CHANNELS.map((ch) => ({
        ...ch,
        enabled: stored === null ? true : (stored[ch.value] ?? true),
      }))
    }

    watch(
      () => customersStore.customer,
      (customer) => loadFromCustomer(customer),
      { immediate: true },
    )

    const save = () => {
      saving.value = true
      const payload: Record<string, boolean> = {}
      for (const ch of channels.value) {
        payload[ch.value] = ch.enabled
      }
      customersStore
        .update({ id: props.customerId, data: { allowedDistributionChannels: payload } as Partial<Company> })
        .then(() => {
          notify({ group: 'success', text: 'Distribution channels updated' })
          modalStore.closeModal(props.id)
        })
        .finally(() => {
          saving.value = false
        })
    }

    const closeModal = () => {
      modalStore.closeModal(props.id)
    }

    return {
      channels,
      saving,
      save,
      closeModal,
    }
  },
})
</script>

